import React, { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardContent, Button, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, IconButton, Dialog, DialogContent, Menu, MenuItem, DialogTitle, DialogActions, DialogContentText } from '@mui/material';
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/api";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import Loading from "../../../components/loading/index";
import SuccessMessage from "../../../components/successMessage";
import ErrorMessage from "../../../components/errorMessage";
import ImageModal from "../image/imageModal";


export default function ImageList() {

    const { patientId } = useParams();
    const navigate = useNavigate();

    const [isLoading, setLoading] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [images, setImages] = useState([]);
    const [isNewImageModalOpen, setIsNewImageModalOpen] = useState(false);
    const [selectedImageToView, setSelectedImageToView] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    const [showModalToDelete, setShowModalToDelete] = useState(false);

    const getImagesFromPatient = useCallback(() => {
        if (!patientId) {
            return;
        }
        setLoading(true);
        api.get(`/images/patient/${patientId}`, {
            headers: { Authorization: sessionStorage.getItem('token') }

        })
        .then((returnFromApi) => {
            setImages(returnFromApi.data);
            setLoading(false)

        })
        .catch((returnFromApi) => {
                setLoading(false);
                console.error("ops! ocorreu um erro" + returnFromApi);
                setErrorMessage(returnFromApi.response.data);
                if (returnFromApi.status === 401) {
                    navigate("/expired-session");
                }        
        });
    }, [patientId, navigate]);


    useEffect(() => {
        getImagesFromPatient();
    }, [patientId, getImagesFromPatient]);

    const onImageSaved = (returnFromApi) => {
        setIsNewImageModalOpen(false);
        if (returnFromApi.data) {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
        } else {
            setShowErrorMessage(true)
            setErrorMessage(returnFromApi.response.data);
        }
        getImagesFromPatient();
    };

    const deleteImage = (imageId) => {
        setLoading(true);
        api.delete(`/images/${imageId}`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            setLoading(false);
            getImagesFromPatient();
        }).catch((returnFromApi) => {
            setLoading(false);
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
            console.error("ops! ocorreu um erro" + returnFromApi);
        });
    };

    const menuClose = () => {
        setAnchorEl(null);
    };

    const confirmDelete = () => {
        setShowModalToDelete(false);
        if (selectedImage) {
            deleteImage(selectedImage.id);
        }
    };

    function openImage(image) {
        setLoading(true);
        api.get(`/images/${image.id}/url`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setLoading(false);
            setSelectedImageToView(returnFromApi.data);
        }).catch((returnFromApi) => {
            setLoading(false);
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
            console.error("ops! ocorreu um erro" + returnFromApi);
        });
    }

    function openModalToDelete() {
        setShowModalToDelete(true);
    }

    return (
        <>
            <Loading isToHidden={!isLoading} />
            {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
            {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardHeader
                    title={<Typography variant="h5" sx={{ fontWeight: 'bold' }}>Imagens do Paciente</Typography>}
                    action={<Button variant="outlined" onClick={() => setIsNewImageModalOpen(true)}>Adicionar Imagem</Button>}
                />
                <CardContent>
                    {!isLoading && (!images || images.length === 0) ? (
                        <Typography align="center" sx={{ py: 4 }}>Nenhuma imagem cadastrada ainda.</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {images.map((image) => (
                                        <TableRow key={image.id} hover>
                                            <TableCell sx={{ width: '20%' }}>{image.date}</TableCell>
                                            <TableCell>{image.description}</TableCell>
                                            <TableCell align="right">
                                                <IconButton onClick={(event) => {
                                                    setAnchorEl(event.currentTarget);
                                                    setSelectedImage(image);
                                                }}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <ImageModal open={isNewImageModalOpen} onCloseImageModal={() => setIsNewImageModalOpen(false)} onImageSaved={onImageSaved} patientId={patientId} />

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={menuClose}>
                <MenuItem onClick={() => {
                    openImage(selectedImage);
                    menuClose();
                }}>Ver Imagem</MenuItem>
                <MenuItem
                    onClick={() => {
                        openModalToDelete();
                        menuClose();
                    }}
                    sx={{ color: 'error.main' }}
                >
                    Deletar Imagem
                </MenuItem>
            </Menu>

            <Dialog open={Boolean(selectedImageToView)} maxWidth="xl" >
               <Tooltip title="Fechar">
                <IconButton 
                    aria-label="close" 
                    onClick={() => setSelectedImageToView(null)} 
                    sx={{ 
                        position: 'absolute', 
                        right: 8, 
                        top: 8,
                        backgroundColor: 'white',
                        color: 'black',
                        borderRadius: '50%',
                        '&:hover': {
                            backgroundColor: 'lightgray',
                        }
                    }}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>
            </Tooltip>
                <img src={selectedImageToView} alt="Imagem" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
            </Dialog>

            <Dialog
                open={showModalToDelete}
                onClose={() => setShowModalToDelete(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirmar Exclusão"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowModalToDelete(false)}>Cancelar</Button>
                    <Button onClick={confirmDelete} color="error">
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}